import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, X, Send } from 'lucide-react';
import { getMarca } from '../utils/marca';
import { postIA } from '../utils/apiIA';

// Asistente flotante "¿cómo se hace?": explica cómo usar el sistema (how-to), alimentado
// con el manual de usuario vía /api/asistente. No consulta ni toca datos.
const MODULOS = {
  '/': 'Dashboard', '/clientes': 'Clientes', '/polizas': 'Pólizas', '/cobros': 'Cobros',
  '/comisiones': 'Comisiones', '/crm': 'CRM', '/reclamos': 'Reclamos', '/solicitudes': 'Solicitudes',
  '/agentes': 'Agentes', '/archivos': 'Archivos', '/reportes': 'Reportes',
  '/contabilidad': 'Contabilidad', '/admin': 'Administración',
};

const SUGERENCIAS = [
  '¿Cómo hago un cambio de prima mensual en una póliza de Vida?',
  '¿Cómo registro una declaración en una póliza declarativa?',
  '¿Cómo genero la comisión de un cobro?',
  '¿Cómo veo el avance de renovaciones del mes?',
];

// ── Render mínimo de Markdown para el chat ──────────────────────────────────
// El modelo responde con Markdown (**negrita**, ## títulos, viñetas). Sin esto se
// veían los asteriscos crudos. Cubre lo que el asistente usa de verdad; las tablas
// se aplanan a una línea porque no caben en el ancho del widget (y el prompt le
// pide que no las use).
function inline(texto) {
  const partes = String(texto).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return partes.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(p)) return (
      <code key={i} style={{ background: 'rgba(var(--silver-rgb),0.18)', padding: '1px 4px', borderRadius: 4, fontSize: 12 }}>{p.slice(1, -1)}</code>
    );
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function Markdown({ texto }) {
  const out = [];
  String(texto || '').split('\n').forEach((linea, i) => {
    const t = linea.trimEnd();
    if (/^\s*(-{3,}|\*{3,})\s*$/.test(t)) {
      out.push(<div key={i} style={{ borderTop: '1px solid rgba(var(--silver-rgb),0.25)', margin: '8px 0' }} />); return;
    }
    const h = t.match(/^\s*(#{1,4})\s+(.*)$/);
    if (h) { out.push(<div key={i} style={{ fontWeight: 800, marginTop: 8, marginBottom: 2 }}>{inline(h[2])}</div>); return; }
    const cita = t.match(/^\s*>\s?(.*)$/);
    if (cita) {
      out.push(<div key={i} style={{ borderLeft: '2px solid rgba(var(--sky-rgb),0.6)', paddingLeft: 8, margin: '3px 0', opacity: .9 }}>{inline(cita[1])}</div>); return;
    }
    const li = t.match(/^\s*[-*]\s+(.*)$/);
    if (li) {
      out.push(<div key={i} style={{ display: 'flex', gap: 6, margin: '2px 0' }}><span>•</span><span>{inline(li[1])}</span></div>); return;
    }
    const ol = t.match(/^\s*(\d+)[.)]\s+(.*)$/);
    if (ol) {
      out.push(<div key={i} style={{ display: 'flex', gap: 6, margin: '2px 0' }}><span style={{ fontWeight: 700 }}>{ol[1]}.</span><span>{inline(ol[2])}</span></div>); return;
    }
    if (/^\s*\|/.test(t)) {                       // fila de tabla → línea simple
      const celdas = t.split('|').map(c => c.trim()).filter(Boolean);
      if (!celdas.length || celdas.every(c => /^:?-{2,}:?$/.test(c))) return;
      out.push(<div key={i} style={{ margin: '2px 0' }}>{inline(celdas.join(' · '))}</div>); return;
    }
    if (!t.trim()) { out.push(<div key={i} style={{ height: 6 }} />); return; }
    out.push(<div key={i} style={{ margin: '2px 0' }}>{inline(t)}</div>);
  });
  return <div>{out}</div>;
}

export default function AsistenteIA() {
  const location = useLocation();
  const modulo = MODULOS[location.pathname] || '';
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);       // [{role:'user'|'assistant', content}]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, loading, open]);

  const enviar = async (texto) => {
    const pregunta = (texto ?? input).trim();
    if (!pregunta || loading) return;
    const historial = msgs.slice(-8);
    setMsgs(m => [...m, { role: 'user', content: pregunta }]);
    setInput('');
    setLoading(true);
    try {
      const out = await postIA('/api/asistente', { pregunta, modulo, historial, marca: getMarca().nombre });
      const respuesta = out?.ok
        ? out.respuesta
        : (out?.reason === 'no_configurado'
            ? 'El asistente aún no está activo (falta configurar la API en el servidor).'
            : 'No pude responder ahora. Reintenta en un momento.');
      setMsgs(m => [...m, { role: 'assistant', content: respuesta }]);
    } catch {
      setMsgs(m => [...m, { role: 'assistant', content: 'Hubo un error de conexión. Reintenta.' }]);
    } finally { setLoading(false); }
  };

  // Botón flotante
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Asistente: ¿cómo se hace?"
        style={{
          position: 'fixed', bottom: 22, right: 22, zIndex: 1400, width: 56, height: 56, borderRadius: '50%',
          border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,var(--sky),var(--steel))',
          color: '#fff', boxShadow: '0 6px 20px rgba(var(--navy-rgb),0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
        <HelpCircle size={26} />
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed', bottom: 22, right: 22, zIndex: 1400, width: 'min(380px, calc(100vw - 32px))',
      height: 'min(560px, calc(100vh - 60px))', display: 'flex', flexDirection: 'column',
      background: 'var(--navy)', border: '1px solid rgba(var(--sky-rgb),0.35)', borderRadius: 14,
      boxShadow: '0 12px 40px rgba(var(--navy-rgb),0.6)', overflow: 'hidden', fontFamily: 'var(--font)',
    }}>
      {/* Header */}
      <div style={{ padding: '12px 14px', background: 'linear-gradient(135deg,var(--steel),var(--navy))', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid rgba(var(--sky-rgb),0.25)' }}>
        <HelpCircle size={20} style={{ color: 'var(--sky-light)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Asistente</div>
          <div style={{ fontSize: 11, color: 'var(--silver)' }}>¿Cómo se hace…? {modulo ? `· ${modulo}` : ''}</div>
        </div>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--silver)', cursor: 'pointer' }}><X size={18} /></button>
      </div>

      {/* Mensajes */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {msgs.length === 0 && (
          <div style={{ color: 'var(--silver)', fontSize: 13 }}>
            <div style={{ marginBottom: 10 }}>Pregúntame cómo hacer algo en el sistema. Ejemplos:</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {SUGERENCIAS.map((s, i) => (
                <button key={i} onClick={() => enviar(s)} style={{
                  textAlign: 'left', background: 'rgba(var(--sky-rgb),0.1)', border: '1px solid rgba(var(--sky-rgb),0.25)',
                  borderRadius: 8, padding: '8px 10px', color: 'var(--sky-light)', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit',
                }}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%',
            background: m.role === 'user' ? 'var(--sky)' : 'rgba(var(--steel-rgb),0.5)',
            color: m.role === 'user' ? '#fff' : 'var(--text)',   // var(--text): se lee en tema claro y oscuro
            border: m.role === 'user' ? 'none' : '1px solid rgba(var(--silver-rgb),0.15)',
            borderRadius: 10, padding: '9px 12px', fontSize: 13, lineHeight: 1.45,
            ...(m.role === 'user' ? { whiteSpace: 'pre-wrap' } : {}),
          }}>{m.role === 'user' ? m.content : <Markdown texto={m.content} />}</div>
        ))}
        {loading && (
          <div style={{ alignSelf: 'flex-start', color: 'var(--silver)', fontSize: 13, padding: '9px 12px' }}>Escribiendo…</div>
        )}
      </div>

      {/* Input */}
      <div style={{ padding: 10, borderTop: '1px solid rgba(var(--sky-rgb),0.2)', display: 'flex', gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && enviar()}
          placeholder="Escribe tu pregunta…" disabled={loading}
          style={{ flex: 1, background: 'rgba(var(--steel-rgb),0.4)', border: '1px solid rgba(var(--silver-rgb),0.25)', borderRadius: 8, padding: '9px 11px', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
        <button onClick={() => enviar()} disabled={loading || !input.trim()}
          style={{ background: 'var(--sky)', border: 'none', borderRadius: 8, width: 40, cursor: loading || !input.trim() ? 'default' : 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: loading || !input.trim() ? 0.5 : 1 }}>
          <Send size={16} />
        </button>
      </div>
      <div style={{ fontSize: 10, color: 'var(--silver)', textAlign: 'center', padding: '0 0 8px' }}>Guía de uso · no consulta datos reales</div>
    </div>
  );
}
